/**
 * Context guards for eases (D§4.12, D§6.8, D§6.11, F§6.7, F§9.3; contract `EaseUse`) and overshoot detection.
 *
 * Every use first checks what the model allows: tween ids 1..29, slices with 0 <= left < right <= 1 (or the
 * whole curve 0..1), no slice Phira would divide by zero on (f(left) == f(right)), finite Bezier handles with
 * x1, x2 in [0, 1]. Then the use's own rule:
 *   speedLegacy        any tween or hold (eased segments are baked on export); never Bezier
 *   speedLegacyGuided  only Linear and Stay (Phira plays legacy speed as straight ramps)
 *   speed170           any tween or hold; never Bezier (Phira's 170 integrator panics)
 *   textRevealErase    nothing that overshoots (Phira panics slicing the text, PH-03)
 *   textOther          anything (overshoot is a checker warning, PH-16)
 *   control            RPE numbers only: no Stay, no partial curve, no Bezier (control points carry a u8 easing)
 *   extraVar           tween ids, slices and Stay; never Bezier (extra.json keyframes have no Bezier fields)
 *   general            anything valid
 *
 * `overshoots` answers whether the curve as Phira evaluates it on x in [0, 1] leaves [0, 1]: Back and Elastic
 * over their whole range; a slice of a non-monotone family (Back, Elastic, Bounce) when its renormalised curve
 * leaves [0, 1] (a slice of Bounce can overshoot a lot); a Bezier when its y(t) leaves [0, 1].
 */

import type { FailReason } from '../util/types';
import { bezierCoord, createBezierCurve } from './bezier';
import { clampedY, sliceIsDegenerate } from './clamped';
import { MAX_RPE_EASING, resolveRpe, ZERO_BEZIER } from './rpeMap';
import { tweenShape } from './tweens';
import type { BezierPoints, Ease, EaseUse } from './types';

/** Fail reason keys (namespace 'ease'; English text in ease.messages.ts). */
export const EASE_REASON = {
  unknownEasing: 'ease.unknownEasing',
  sliceOutOfRange: 'ease.sliceOutOfRange',
  sliceEmpty: 'ease.sliceEmpty',
  sliceDegenerate: 'ease.sliceDegenerate',
  bezierNotFinite: 'ease.bezierNotFinite',
  bezierXOutOfRange: 'ease.bezierXOutOfRange',
  noBezierOnSpeed: 'ease.noBezierOnSpeed',
  speedGuidedLinearOnly: 'ease.speedGuidedLinearOnly',
  textRevealOvershoot: 'ease.textRevealOvershoot',
  controlNoBezier: 'ease.controlNoBezier',
  controlNoHold: 'ease.controlNoHold',
  controlNoSlice: 'ease.controlNoSlice',
  extraVarNoBezier: 'ease.extraVarNoBezier',
} as const;

const OVERSHOOT_TOLERANCE = 1e-6;
const OVERSHOOT_SAMPLES = 1024;

function outsideUnit(y: number): boolean {
  return !Number.isFinite(y) || y < -OVERSHOOT_TOLERANCE || y > 1 + OVERSHOOT_TOLERANCE;
}

/** Golden-section search for the extreme of f on [a, b] (sign = 1 for a maximum, -1 for a minimum). */
function extremeOn(f: (x: number) => number, a: number, b: number, sign: 1 | -1): number {
  const g = (Math.sqrt(5) - 1) / 2;
  let lo = a;
  let hi = b;
  let c = hi - g * (hi - lo);
  let d = lo + g * (hi - lo);
  for (let i = 0; i < 60; i++) {
    if (sign * f(c) > sign * f(d)) hi = d;
    else lo = c;
    c = hi - g * (hi - lo);
    d = lo + g * (hi - lo);
  }
  return f((lo + hi) / 2);
}

/** True when f leaves [0, 1] on x in [0, 1] (dense samples plus refinement of every local extremum near 0 or 1). */
export function curveLeavesUnit(f: (x: number) => number): boolean {
  const ys: number[] = [];
  for (let k = 0; k <= OVERSHOOT_SAMPLES; k++) {
    const y = f(k / OVERSHOOT_SAMPLES);
    if (outsideUnit(y)) return true;
    ys.push(y);
  }
  for (let k = 1; k < OVERSHOOT_SAMPLES; k++) {
    const y = ys[k] ?? 0;
    const prev = ys[k - 1] ?? 0;
    const next = ys[k + 1] ?? 0;
    const a = (k - 1) / OVERSHOOT_SAMPLES;
    const b = (k + 1) / OVERSHOOT_SAMPLES;
    if (y >= prev && y >= next && y > 0.99 && outsideUnit(extremeOn(f, a, b, 1))) return true;
    if (y <= prev && y <= next && y < 0.01 && outsideUnit(extremeOn(f, a, b, -1))) return true;
  }
  return false;
}

function bezierOvershoots(p: BezierPoints): boolean {
  const [, y1, , y2] = p;
  // y'(t) / 3 = A t^2 + B t + C on the curve from (0, 0) to (1, 1)
  const a = 3 * y1 - 3 * y2 + 1;
  const b = 2 * y2 - 4 * y1;
  const c = y1;
  const roots: number[] = [];
  if (Math.abs(a) < 1e-12) {
    if (Math.abs(b) > 1e-12) roots.push(-c / b);
  } else {
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const s = Math.sqrt(disc);
      roots.push((-b + s) / (2 * a), (-b - s) / (2 * a));
    }
  }
  for (const t of roots) if (t > 0 && t < 1 && outsideUnit(bezierCoord(y1, y2, t))) return true;
  const curve = createBezierCurve(p);
  return curveLeavesUnit((x) => curve.y(x));
}

/** True when the ease, as Phira evaluates it on x in [0, 1], produces values outside [0, 1]. */
export function overshoots(e: Ease): boolean {
  switch (e.kind) {
    case 'hold':
      return false;
    case 'bezier':
      return bezierOvershoots(e.p);
    case 'tween': {
      const desc = resolveRpe(e.id, e.left, e.right, 0, ZERO_BEZIER);
      if (desc.kind === 'bezier') return bezierOvershoots(desc.p);
      const { family } = tweenShape(desc.tween);
      if (family === null) return false;
      const nonMonotone = family === 'back' || family === 'elastic' || family === 'bounce';
      if (desc.kind !== 'clamped') return family === 'back' || family === 'elastic';
      if (!nonMonotone) return false;
      return curveLeavesUnit((x) => clampedY(desc.tween, desc.l, desc.r, x));
    }
  }
}

function reason(key: string, params?: Readonly<Record<string, string | number>>): FailReason {
  return params === undefined ? { key } : { key, params };
}

function isDefaultSlice(e: Ease & { readonly kind: 'tween' }): boolean {
  return e.left === 0 && e.right === 1;
}

function checkTween(e: Ease & { readonly kind: 'tween' }, use: EaseUse): FailReason | null {
  if (!Number.isInteger(e.id) || e.id < 1 || e.id > MAX_RPE_EASING) {
    return reason(EASE_REASON.unknownEasing, { id: Number.isFinite(e.id) ? e.id : String(e.id) });
  }
  const defaultSlice = isDefaultSlice(e);
  if (!defaultSlice) {
    if (!(e.left >= 0 && e.left <= 1 && e.right >= 0 && e.right <= 1)) return reason(EASE_REASON.sliceOutOfRange);
    if (!(e.left < e.right)) return reason(EASE_REASON.sliceEmpty);
  }
  if (use === 'speedLegacyGuided' && e.id !== 1) return reason(EASE_REASON.speedGuidedLinearOnly);
  if (use === 'control' && !defaultSlice) return reason(EASE_REASON.controlNoSlice);
  if (!defaultSlice && sliceIsDegenerate(e.id, e.left, e.right)) return reason(EASE_REASON.sliceDegenerate);
  if (use === 'textRevealErase' && overshoots(e)) return reason(EASE_REASON.textRevealOvershoot);
  return null;
}

function checkBezier(p: BezierPoints, use: EaseUse): FailReason | null {
  switch (use) {
    case 'speedLegacy':
    case 'speedLegacyGuided':
    case 'speed170':
      return reason(EASE_REASON.noBezierOnSpeed);
    case 'control':
      return reason(EASE_REASON.controlNoBezier);
    case 'extraVar':
      return reason(EASE_REASON.extraVarNoBezier);
    case 'general':
    case 'textRevealErase':
    case 'textOther':
      break;
  }
  if (!p.every((v) => Number.isFinite(v))) return reason(EASE_REASON.bezierNotFinite);
  if (p[0] < 0 || p[0] > 1 || p[2] < 0 || p[2] > 1) return reason(EASE_REASON.bezierXOutOfRange);
  if (use === 'textRevealErase' && bezierOvershoots(p)) return reason(EASE_REASON.textRevealOvershoot);
  return null;
}

/** Validates an ease for a use; null when allowed, else the reason (MessageRef in namespace 'ease'). */
export function validateFor(e: Ease, use: EaseUse): FailReason | null {
  switch (e.kind) {
    case 'hold':
      return use === 'control' ? reason(EASE_REASON.controlNoHold) : null;
    case 'tween':
      return checkTween(e, use);
    case 'bezier':
      return checkBezier(e.p, use);
  }
}
